import type {FC, MouseEventHandler, ReactNode} from 'react';
import {createPortal} from 'react-dom';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {FeatureController, type FeatureControllerProps} from '~library/molecules/FeatureController';
import {ResponsivePanel} from '~library/organisms/ResponsivePanel';

import './InvitationWidget.sass';

export type InvitationWidgetOverlayConfig = {
  title: string;
  subtitle?: string;
  content: ReactNode;
  qaId: string;
  isOpen: boolean;
  onSubmit: () => void;
  onClose: () => void;
  postSubmitCallback?: () => void;
  ctas?: {
    ctaText: string;
    onClick?: MouseEventHandler<HTMLButtonElement>;
    ctaQaId: string;
    ctaVariant: 'submit' | 'remove';
    disabled?: boolean;
    hide?: boolean; // If true, this button will not be rendered.
  }[];
};

const Controller: FC<InvitationWidgetOverlayConfig> = ({
  title,
  subtitle,
  content,
  qaId,
  isOpen,
  onSubmit,
  onClose,
  ctas = [],
}) => {
  const handleSubmit: MouseEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSubmit();
  };

  return createPortal(
    <ResponsivePanel
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      renderMobileViewAs="bottom-sheet"
      modalId={qaId}
      showCover
    >
      <form onSubmit={handleSubmit} className="invitation-widget__overlay-form">
        <div className="invitation-widget__overlay-content">
          {subtitle && <Typography variant="paragraph2">{subtitle}</Typography>}
          {content}
        </div>
        {ctas.length > 0 && (
          <div className="invitation-widget__overlay-actions">
            {ctas
              .filter(({hide}) => !hide)
              .map(({ctaText, onClick, ctaQaId, ctaVariant, disabled = false}) => (
                <Button
                  key={ctaQaId}
                  className={`invitation-widget__button__${ctaVariant}`}
                  onClick={onClick}
                  data-qa-id={ctaQaId}
                  variant={ctaVariant === 'remove' ? 'transparent' : 'primary'}
                  size="large"
                  formSubmit={ctaVariant === 'submit'}
                  type={ctaVariant === 'submit' ? 'submit' : 'button'}
                  fullWidth={ctaVariant === 'submit'}
                  disabled={disabled}
                >
                  {ctaText}
                </Button>
              ))}
          </div>
        )}
      </form>
    </ResponsivePanel>,
    document.body
  );
};

interface InvitationWidgetToggleProps extends FeatureControllerProps {
  // If overlay is defined, we use this over controllerConfig to configure the editor panel.
  // We will want to slowly prune out uses of this to move towards controllerConfig,
  // but this is here for now for backwards compatibility.
  overlay?: ReactNode;
  controllerConfig?: InvitationWidgetOverlayConfig;
  renderOverlayOnly?: boolean; // If true, the FeatureController will not be rendered, and the overlay will be controlled externally.
}

const Toggle: FC<InvitationWidgetToggleProps> = ({
  overlay,
  controllerConfig,
  renderOverlayOnly = false,
  ...toggleProps
}) => (
  <>
    {!renderOverlayOnly && <FeatureController {...toggleProps} />}
    {overlay ?? (controllerConfig ? <Controller {...controllerConfig} /> : null)}
  </>
);
Toggle.displayName = 'InvitationWidgetController';

// TODO: Implement when we have widgets that will be displayed as their own component in the Invitation View
interface InvitationWidgetDisplayProps {}

// TODO: Implement when we have widgets that will be displayed as their own component in the Invitation View
const Display: FC<InvitationWidgetDisplayProps> = () => null;
Display.displayName = 'InvitationWidgetDisplay';

export const InvitationWidget = {
  Toggle,
  Controller,
  Display,
};
